// src/DatadogViewTracker.tsx
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";

/**
 * Maps route paths to human-readable page names for Datadog RUM tracking
 */
const PAGE_NAME_MAP: Record<string, string> = {
  "/": "Landing Page",
  "/shop": "Shop Page",
  "/pdf": "PDF Page",
  "/impersonate": "Login Impersonate Page",
  "/social": "Social Poster Page",
  "/social-voucher": "Social Voucher Poster Page",
  "/verify": "Email Verification Page",
  "/payment": "Payment Approval Page",
  "/fandbhongkong": "Onboarding Form Page",
};

/**
 * Maps dashboard page parameters to human-readable names
 */
const DASHBOARD_PAGE_MAP: Record<string, string> = {
  overview: "Dashboard Overview",
  "create-voucher": "Create Voucher Campaign",
  analytics: "Analytics Dashboard",
  programs: "Manage Programs",
  vouchers: "Manage Vouchers",
  "user-management": "User Management",
  "create-milestone-stamp-card": "Create Milestone Stamp Card",
};

/**
 * Maps wizard names to human-readable names
 */
const WIZARD_NAME_MAP: Record<string, string> = {
  expresslaunch: "Express Launch Wizard",
  checkin: "Checkin Wizard",
  loyalty: "Loyalty Setup Wizard",
  voucher: "Voucher Creation Wizard",
  milestone: "Milestone Setup Wizard",
};

/**
 * Generates a descriptive view name based on the current route.
 *
 * Names come from the pathname, not from `useParams()`: this component renders
 * as a sibling of `<Routes>` rather than inside a matched route, so params is
 * always empty. Reading it is what produced "Wizard: undefined" and a flat
 * "Dashboard" for every sub-page in Datadog.
 * @param {string} pathname - The current pathname from the route.
 * @returns {string} A descriptive view name for Datadog tracking.
 */
export function generateViewName(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);

  // Handle wizard routes
  if (segments[0] === "wizard") {
    const wizardName = (segments[1] ?? "").toLowerCase();
    return WIZARD_NAME_MAP[wizardName] || `Wizard: ${wizardName || "unknown"}`;
  }

  // Handle dashboard routes with sub-pages
  if (segments[0] === "dashboard") {
    const page = segments[1];
    const id = segments[2];

    if (page) {
      const pageName = DASHBOARD_PAGE_MAP[page] || page;
      if (id) {
        return `${pageName} (ID: ${id})`;
      }
      return pageName;
    }
    return "Dashboard";
  }

  // Handle verification routes with tokens
  if (segments[0] === "verify") {
    return segments[1]
      ? "Email Verification (with token)"
      : "Email Verification";
  }

  // Handle direct page routes
  const basePath = pathname.split("?")[0]; // Remove query params for mapping
  const mappedName = basePath ? PAGE_NAME_MAP[basePath] : undefined;

  if (mappedName) {
    return mappedName;
  }

  // Fallback to pathname if no mapping exists
  return pathname;
}

export default function DatadogViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const viewName = generateViewName(location.pathname);

    // Start the view with a descriptive name
    datadogRum.startView(viewName);

    // Add custom attributes for better tracking
    datadogRum.addAction("page_view", {
      page_name: viewName,
      pathname: location.pathname,
      search: location.search,
      timestamp: Date.now(),
    });
  }, [location]);

  return null;
}
